/*
 * Cart drawer redesign (see assets/mos-cart-drawer.css and snippets/cart-form.liquid,
 * snippets/cart-subtotal.liquid).
 *
 * Every cart change replaces the innerHTML of #AjaxCartForm and #AjaxCartSubtotal inside the
 * drawer, so nothing here may bind to elements that live in those two containers: clicks are
 * delegated on document and re-renders are caught with MutationObservers. Elements are always
 * looked up lazily because the drawer can be absent (popup mode) or re-created (Theme Editor).
 */
(function () {
  'use strict';

  var DRAWER = '#site-cart-sidebar';

  /* ---------------------------------------------------------------- */
  /* <mos-cart-ship>: the slim free-shipping line at the top of the body */
  /* ---------------------------------------------------------------- */

  // Same formatter as assets/component-shipping-notice.js (not edited, so copied here).
  function formatMoney(cents, format) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }

    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || '${{amount}}';

    function formatWithDelimiters(number, precision, thousands, decimal) {
      thousands = thousands || ',';
      decimal = decimal || '.';

      if (isNaN(number) || number === null) {
        return 0;
      }

      number = (number / 100.0).toFixed(precision);

      var parts = number.split('.');
      var dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsAmount = parts[1] ? decimal + parts[1] : '';

      return dollarsAmount + centsAmount;
    }

    var match = formatString.match(placeholderRegex);
    if (!match) {
      return formatString;
    }

    switch (match[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'");
        break;
    }

    return formatString.replace(placeholderRegex, value);
  }

  var CHECK_ICON =
    '<svg class="mos-cd-ship__ico" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#6f8a6a" ' +
    'stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
    '<path d="M5 12l5 5L20 7"/></svg>';

  function shipRow(icon, text) {
    return '<div class="mos-cd-ship__row">' + icon + '<span>' + text + '</span></div>';
  }

  function shipBar(percent) {
    return '<div class="mos-cd-ship__bar"><span style="width:' + percent + '%"></span></div>';
  }

  if (typeof customElements !== 'undefined' && !customElements.get('mos-cart-ship')) {
    customElements.define(
      'mos-cart-ship',
      class MosCartShip extends HTMLElement {
        connectedCallback() {
          this.render();
        }

        render() {
          var threshold = Number(this.getAttribute('data-free-shipping'));
          var cartTotal = Number(this.getAttribute('data-cart-total-amount'));
          var rate = window.Shopify && Shopify.currency && Shopify.currency.rate ? Number(Shopify.currency.rate) : 1;
          var scaled = Math.round(threshold * rate);
          // Same math as component-shipping-notice.js: negative means still below the threshold.
          var remaining = cartTotal - scaled;

          if (this.hasAttribute('data-bundle')) {
            this.innerHTML = shipRow(CHECK_ICON, 'Free shipping + Mos app access with Foundation');
          } else if (this.hasAttribute('data-subscription')) {
            this.innerHTML = shipRow(CHECK_ICON, 'Free shipping on your subscription');
          } else if (remaining >= 0) {
            this.innerHTML = shipRow(CHECK_ICON, 'You’ve unlocked free shipping') + shipBar(100);
          } else {
            var format = window.KROWN && KROWN.settings && KROWN.settings.shop_money_format;
            var percent = scaled > 0 ? Math.max(0, Math.min(100, Math.round((cartTotal / scaled) * 100))) : 0;
            this.innerHTML =
              shipRow('', formatMoney(Math.abs(remaining), format) + ' away from free shipping') + shipBar(percent);
          }
        }
      }
    );
  }

  /* ---------------------------------------------------------------- */
  /* Discount code toggle in the footer                                */
  /* ---------------------------------------------------------------- */

  var discountOpen = false;

  function discountPanel() {
    return document.querySelector(DRAWER + ' #CartDrawer-DiscountPanel');
  }

  function setDiscountOpen(open) {
    var panel = discountPanel();
    if (!panel) return;
    panel.classList.toggle('is-open', open);
    var link = document.querySelector(DRAWER + ' .mos-cd-disc-link');
    if (link) link.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  document.addEventListener('click', function (e) {
    var link = e.target.closest && e.target.closest(DRAWER + ' .mos-cd-disc-link');
    if (!link) return;
    var panel = discountPanel();
    if (!panel) return;

    discountOpen = !panel.classList.contains('is-open');
    setDiscountOpen(discountOpen);

    if (discountOpen) {
      // setTimeout rather than requestAnimationFrame: rAF does not run in background tabs.
      setTimeout(function () {
        var input = document.querySelector(DRAWER + ' #CartDrawer-Discount');
        if (input) input.focus();
      }, 250);
    }
  });

  // The footer is re-rendered after every cart change; re-apply the open state, skipping the
  // transition for that one frame so the panel doesn't visibly collapse and re-open.
  function applyDiscountStateAfterRender() {
    var panel = discountPanel();
    if (!panel) return;

    // A server-rendered open panel means codes are applied: treat that as the user's state.
    if (panel.classList.contains('is-open')) {
      discountOpen = true;
    }

    if (discountOpen) {
      panel.classList.add('mos-cd-disc--instant');
      setDiscountOpen(true);
      setTimeout(function () {
        panel.classList.remove('mos-cd-disc--instant');
      }, 50);
    }
  }

  /* ---------------------------------------------------------------- */
  /* "Added" highlight on new / increased lines                        */
  /* ---------------------------------------------------------------- */

  var pendingAdd = false;
  var snapshot = {};
  // Set by quick add so the drawer body scrolls up to the freshly added line after the re-render.
  var scrollBodyToTopAfterRender = false;

  function takeSnapshot() {
    var map = {};
    document.querySelectorAll(DRAWER + ' [data-js-cart-item]').forEach(function (item) {
      map[item.getAttribute('data-id')] = Number(item.getAttribute('data-qty')) || 0;
    });
    return map;
  }

  function highlightAddedLines() {
    if (pendingAdd) {
      document.querySelectorAll(DRAWER + ' [data-js-cart-item]').forEach(function (item) {
        var key = item.getAttribute('data-id');
        var qty = Number(item.getAttribute('data-qty')) || 0;
        if (!(key in snapshot) || qty > snapshot[key]) {
          item.classList.add('is-new');
          setTimeout(function () {
            item.classList.remove('is-new');
          }, 1600);
        }
      });
    }
    pendingAdd = false;
    snapshot = takeSnapshot();
  }

  // The PDP add dispatches this synchronously right after swapping the HTML, i.e. before the
  // MutationObserver callbacks below run, so the flag is already set when they look at it.
  document.addEventListener('krown:cart:add', function () {
    pendingAdd = true;
  });

  /* ---------------------------------------------------------------- */
  /* Quick add from the "Pairs well with" rows                         */
  /* ---------------------------------------------------------------- */

  document.addEventListener('click', function (e) {
    var button = e.target.closest && e.target.closest(DRAWER + ' [data-mos-quick-add]');
    if (!button) return;
    e.preventDefault();
    if (button.classList.contains('is-loading')) return;

    var variantId = button.getAttribute('data-variant-id');
    var sellingPlan = button.getAttribute('data-selling-plan');
    var row = button.closest('.mos-cd-sg__row');

    var oldError = row && row.parentNode.querySelector('.mos-cd-sg__error');
    if (oldError) oldError.remove();

    button.classList.add('is-loading');
    button.textContent = 'Adding…';

    function restore() {
      button.classList.remove('is-loading');
      button.textContent = '+ Add';
    }

    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

    fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [
          {
            id: Number(variantId),
            quantity: 1,
            selling_plan: sellingPlan ? Number(sellingPlan) : undefined
          }
        ]
      })
    })
      .then(function (response) {
        return response.json().catch(function () { return {}; }).then(function (data) {
          return { ok: response.ok, data: data };
        });
      })
      .then(function (result) {
        if (!result.ok) {
          restore();
          if (row) {
            var error = document.createElement('div');
            error.className = 'mos-cd-sg__error';
            error.textContent = result.data.description || result.data.message || 'Could not add this product.';
            row.insertAdjacentElement('afterend', error);
          }
          return;
        }

        pendingAdd = true;

        if (typeof window.refreshCart === 'function') {
          window.refreshCart(true);
          // refreshCart swaps the HTML asynchronously; scroll once the new content is in.
          scrollBodyToTopAfterRender = true;
        } else {
          window.location.reload();
        }
      })
      .catch(function () {
        restore();
      });
  });

  function scrollBodyToTop() {
    if (!scrollBodyToTopAfterRender) return;
    scrollBodyToTopAfterRender = false;
    var body = document.querySelector(DRAWER + ' .sidebar__body');
    if (body && typeof body.scrollTo === 'function') {
      body.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  /* ---------------------------------------------------------------- */
  /* Wiring: observers on the two re-rendered containers               */
  /* ---------------------------------------------------------------- */

  var observedForm = null;
  var observedSubtotal = null;
  var formObserver = null;
  var subtotalObserver = null;

  function observe() {
    if (!document.querySelector(DRAWER)) return;

    var form = document.querySelector(DRAWER + ' #AjaxCartForm');
    if (form && form !== observedForm) {
      if (formObserver) formObserver.disconnect();
      observedForm = form;
      snapshot = takeSnapshot();
      formObserver = new MutationObserver(function () {
        highlightAddedLines();
        scrollBodyToTop();
      });
      formObserver.observe(form, { childList: true });
    }

    var subtotal = document.querySelector(DRAWER + ' #AjaxCartSubtotal');
    if (subtotal && subtotal !== observedSubtotal) {
      if (subtotalObserver) subtotalObserver.disconnect();
      observedSubtotal = subtotal;
      // Server-rendered open panel (codes already applied) counts as open.
      var panel = discountPanel();
      if (panel && panel.classList.contains('is-open')) discountOpen = true;
      subtotalObserver = new MutationObserver(applyDiscountStateAfterRender);
      subtotalObserver.observe(subtotal, { childList: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observe);
  } else {
    observe();
  }

  // Theme Editor re-renders sections (and with them possibly the drawer): re-attach lazily.
  document.addEventListener('shopify:section:load', observe);
})();
